import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { AnalyticsChart, ProgressRing } from '../components/charts/AnalyticsChart';
import { Link } from 'react-router-dom';
import { 
    Users, 
    BookOpen, 
    Layers, 
    Trophy, 
    TrendingUp, 
    Mic, 
    MessageSquare, 
    Target,
    GraduationCap,
    Play,
    ArrowRight,
    Activity,
    BarChart3,
    Calendar,
    Clock,
    Download,
    FileText,
    Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { CourseRecommendations } from '../components/ai/CourseRecommendations';
import { CurriculumProgress } from '../components/dashboard/CurriculumProgress';
import AdminComprehensiveDashboard from '../components/dashboard/AdminComprehensiveDashboard';
import UserComprehensiveDashboard from '../components/dashboard/UserComprehensiveDashboard';
import CareerJourneyDashboard from '../components/dashboard/CareerJourneyDashboard';
import { QualityTasksBanner } from '../components/quality/DueSoonBanner';
import { EEOCDueBanner } from '../components/eeoc/EEOCCycleBanner';

const Dashboard = () => {
    const { user, api, isAdmin, isTrainer } = useAuth();
    const [stats, setStats] = useState(null);
    const [progress, setProgress] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchDashboardData = useCallback(async () => {
        try {
            const promises = [api.get('/dashboard/stats')];
            
            if (!isAdmin && !isTrainer) {
                promises.push(api.get('/progress'));
                promises.push(api.get('/learning-profile'));
            }
            
            if (isAdmin || isTrainer) {
                promises.push(api.get('/reports/overview'));
            }
            
            const results = await Promise.all(promises);
            setStats(results[0].data);
            
            if (!isAdmin && !isTrainer) {
                setProgress(results[1].data);
                setAnalytics(results[2]?.data || null);
            } else {
                setAnalytics(results[1]?.data || null);
            }
        } catch (error) {
            console.error('Failed to fetch dashboard data:', error);
            toast.error('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    }, [api, isAdmin, isTrainer]);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    if (loading) {
        return (
            <MainLayout>
                <div className="flex items-center justify-center h-[60vh]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="space-y-8 animate-fade-in" data-testid="dashboard">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-4xl font-heading font-bold tracking-tight">
                            Welcome back, <span className="text-gradient">{user?.name}</span>
                        </h1>
                        <p className="text-muted-foreground mt-2">
                            {isAdmin && "Manage your training platform and monitor progress"}
                            {isTrainer && "Create content and track trainee performance"}
                            {!isAdmin && !isTrainer && "Continue your recruitment training journey"}
                        </p>
                    </div>
                    {(isAdmin || isTrainer) && (
                        <Button variant="outline" className="gap-2" asChild>
                            <Link to="/reports">
                                <FileText className="h-4 w-4" />
                                View Reports
                            </Link>
                        </Button>
                    )}
                </div>

                <QualityTasksBanner />
                <EEOCDueBanner />

                {/* Admin Comprehensive Dashboard */}
                {isAdmin && (
                    <AdminComprehensiveDashboard />
                )}

                {/* Trainer Dashboard - Keep existing simpler view */}
                {isTrainer && !isAdmin && stats && (
                    <>
                        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                            <Card className="card-hover">
                                <CardHeader className="flex flex-row items-center justify-between pb-2">
                                    <CardTitle className="text-sm font-medium text-muted-foreground">My Topics</CardTitle>
                                    <BookOpen className="h-5 w-5 text-primary" />
                                </CardHeader>
                                <CardContent>
                                    <div className="text-3xl font-bold font-heading">{stats.stats?.topics_created || 0}</div>
                                    <p className="text-xs text-muted-foreground mt-1">Topics created</p>
                                </CardContent>
                            </Card>

                            <Card className="card-hover">
                                <CardHeader className="flex flex-row items-center justify-between pb-2">
                                    <CardTitle className="text-sm font-medium text-muted-foreground">My Modules</CardTitle>
                                    <Layers className="h-5 w-5 text-secondary" />
                                </CardHeader>
                                <CardContent>
                                    <div className="text-3xl font-bold font-heading">{stats.stats?.modules_created || 0}</div>
                                    <p className="text-xs text-muted-foreground mt-1">Modules created</p>
                                </CardContent>
                            </Card>

                            <Card className="card-hover">
                                <CardHeader className="flex flex-row items-center justify-between pb-2">
                                    <CardTitle className="text-sm font-medium text-muted-foreground">Total Trainees</CardTitle>
                                    <Users className="h-5 w-5 text-emerald-500" />
                                </CardHeader>
                                <CardContent>
                                    <div className="text-3xl font-bold font-heading">{stats.stats?.total_trainees || 0}</div>
                                    <p className="text-xs text-muted-foreground mt-1">Active learners</p>
                                </CardContent>
                            </Card>
                        </div>
                    </>
                )}

                {/* User Dashboard - Unified Career Journey View */}
                {!isAdmin && !isTrainer && (
                    <CareerJourneyDashboard />
                )}

                {/* Quick Actions - Admin & Trainer Only (User has integrated quick actions in CareerJourneyDashboard) */}
                {(isAdmin || isTrainer) && (
                    <div className="grid gap-4 md:grid-cols-3">
                        {isAdmin && (
                            <>
                                <Link to="/users">
                                    <Card className="card-hover cursor-pointer group">
                                        <CardContent className="flex items-center justify-between p-6">
                                            <div className="flex items-center gap-4">
                                                <div className="p-3 rounded-xl bg-primary/10">
                                                    <Users className="h-6 w-6 text-primary" />
                                                </div>
                                                <div>
                                                    <h3 className="font-semibold">Manage Users</h3>
                                                    <p className="text-sm text-muted-foreground">Add or edit users</p>
                                                </div>
                                            </div>
                                            <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                                        </CardContent>
                                    </Card>
                                </Link>
                                <Link to="/topics">
                                    <Card className="card-hover cursor-pointer group">
                                        <CardContent className="flex items-center justify-between p-6">
                                            <div className="flex items-center gap-4">
                                                <div className="p-3 rounded-xl bg-secondary/10">
                                                    <BookOpen className="h-6 w-6 text-secondary" />
                                                </div>
                                                <div>
                                                    <h3 className="font-semibold">Manage Topics</h3>
                                                    <p className="text-sm text-muted-foreground">Create training content</p>
                                                </div>
                                            </div>
                                            <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-secondary transition-colors" />
                                        </CardContent>
                                    </Card>
                                </Link>
                                <Link to="/reports">
                                    <Card className="card-hover cursor-pointer group">
                                        <CardContent className="flex items-center justify-between p-6">
                                            <div className="flex items-center gap-4">
                                                <div className="p-3 rounded-xl bg-emerald-500/10">
                                                    <TrendingUp className="h-6 w-6 text-emerald-500" />
                                                </div>
                                                <div>
                                                    <h3 className="font-semibold">View Reports</h3>
                                                    <p className="text-sm text-muted-foreground">Analytics & insights</p>
                                                </div>
                                            </div>
                                            <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-emerald-500 transition-colors" />
                                        </CardContent>
                                    </Card>
                                </Link>
                            </>
                        )}

                        {isTrainer && !isAdmin && (
                            <>
                                <Link to="/topics">
                                    <Card className="card-hover cursor-pointer group">
                                        <CardContent className="flex items-center justify-between p-6">
                                            <div className="flex items-center gap-4">
                                                <div className="p-3 rounded-xl bg-primary/10">
                                                    <BookOpen className="h-6 w-6 text-primary" />
                                                </div>
                                                <div>
                                                    <h3 className="font-semibold">Create Topic</h3>
                                                    <p className="text-sm text-muted-foreground">Add training content</p>
                                                </div>
                                            </div>
                                            <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                                        </CardContent>
                                    </Card>
                                </Link>
                                <Link to="/modules">
                                    <Card className="card-hover cursor-pointer group">
                                        <CardContent className="flex items-center justify-between p-6">
                                            <div className="flex items-center gap-4">
                                                <div className="p-3 rounded-xl bg-secondary/10">
                                                    <Layers className="h-6 w-6 text-secondary" />
                                                </div>
                                                <div>
                                                    <h3 className="font-semibold">Create Module</h3>
                                                    <p className="text-sm text-muted-foreground">Organize topics</p>
                                                </div>
                                            </div>
                                            <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-secondary transition-colors" />
                                        </CardContent>
                                    </Card>
                                </Link>
                                <Link to="/reports">
                                    <Card className="card-hover cursor-pointer group">
                                        <CardContent className="flex items-center justify-between p-6">
                                            <div className="flex items-center gap-4">
                                                <div className="p-3 rounded-xl bg-emerald-500/10">
                                                    <TrendingUp className="h-6 w-6 text-emerald-500" />
                                                </div>
                                                <div>
                                                    <h3 className="font-semibold">Trainee Reports</h3>
                                                    <p className="text-sm text-muted-foreground">Track progress</p>
                                                </div>
                                            </div>
                                            <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-emerald-500 transition-colors" />
                                        </CardContent>
                                    </Card>
                                </Link>
                            </>
                        )}
                    </div>
                )}
            </div>
        </MainLayout>
    );
};

export default Dashboard;
